import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/Init';
import AppLayout from '../components/oracle/AppLayout';
import BackButton from '../components/oracle/BackButton';
import LogoSpinner from '../components/oracle/LogoSpinner';
import HeaderTitle from '../components/oracle/HeaderTitle';
import VaultFocusView from '../components/vault/VaultFocusView';
import UnlockGateModal from '../components/oracle/UnlockGateModal';
import ScanRevealModal from '../components/oracle/ScanRevealModal';
import EmptyState from '../components/oracle/EmptyState';
import { VAULT_ERROR_CODES } from '../contracts/vaultApi';
import { ROUTES } from '../contracts/routes';
import { COPY } from '../contracts/copyMap';

// "Top N%" chip text for the reveal, or null when the client-side model did
// not run on this scan.
function percentileTopText(learnedPercentile) {
  if (typeof learnedPercentile !== 'number') return null;
  const top = Math.max(1, Math.round((1 - learnedPercentile) * 100));
  return `Top ${top}%`;
}

// Detail-page date/time (UTC-stable so CI/test output is deterministic).
function fmtDetailDate(iso) {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}
function fmtDetailTime(iso) {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
  });
}

// Single-scan detail page. Renders the editorial VaultFocusView for the
// URL-pinned scan — the full breakdown counterpart to the /vault browse grid.
// The page header is a back arrow + FOCUS SCAN date/time (no VAULT title/tabs);
// navigation between scans happens via the grid.
const VaultEntry = () => {
  const navigate = useNavigate();
  const { scan_id: scanId } = useParams();
  const [detailState, setDetailState] = useState({ phase: 'loading' });
  const [resignAttempted, setResignAttempted] = useState(false);
  // The payoff. Set by the judging surface when the tenth reading opened this
  // scan: the theatrical count-up becomes the reward for the work, using the
  // EXISTING ScanRevealModal rather than a second reveal component.
  const [searchParams, setSearchParams] = useSearchParams();
  const celebrate = searchParams.get('reveal') === '1';

  const loadDetail = useCallback(async () => {
    setDetailState({ phase: 'loading' });
    setResignAttempted(false);
    try {
      const getEntry = httpsCallable(functions, 'getVaultEntry');
      const result = await getEntry({ scan_id: scanId });
      const data = result && result.data;
      if (data && data.status === 'success') {
        // A SEALED reading comes back with an `unlock` block and no verdict at
        // all — see getVaultEntry. Render the gate rather than the focus view,
        // which would otherwise try to read fields that are simply absent.
        if (data.unlock && data.unlock.state === 'locked') {
          setDetailState({ phase: 'locked', unlock: data.unlock });
          return;
        }
        setDetailState({ phase: 'ready', detail: data });
        return;
      }
      if (data && data.status === 'error') {
        setDetailState({ phase: 'error', code: data.error_code, message: data.error_message });
        return;
      }
      setDetailState({ phase: 'error', code: 'INTERNAL', message: COPY.REJECT_NETWORK_SUBTITLE });
    } catch (err) {
      setDetailState({
        phase: 'error',
        code: 'INTERNAL',
        message: (err && err.message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    }
  }, [scanId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleImageError = useCallback(async () => {
    if (resignAttempted || detailState.phase !== 'ready') return;
    setResignAttempted(true);
    try {
      const resign = httpsCallable(functions, 'resignProcessedUrl');
      const result = await resign({ scan_id: scanId });
      const data = result && result.data;
      if (data && data.status === 'success') {
        setDetailState((prev) => ({
          ...prev,
          detail: { ...prev.detail, processed_image_url: data.processed_image_url },
        }));
      }
    } catch {
      // best-effort
    }
  }, [resignAttempted, scanId, detailState.phase]);

  if (detailState.phase === 'loading') {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <LogoSpinner />
        </div>
      </AppLayout>
    );
  }

  if (detailState.phase === 'locked') {
    return (
      <AppLayout>
        <UnlockGateModal
          open
          surface="vault_entry"
          unlock={detailState.unlock}
          onJudge={() => navigate(`${ROUTES.JUDGE}?unlock=${scanId}`)}
          onDismiss={() => navigate(ROUTES.VAULT)}
        />
      </AppLayout>
    );
  }

  if (detailState.phase === 'error') {
    if (detailState.code === VAULT_ERROR_CODES.FORBIDDEN) {
      return (
        <AppLayout>
          <div className="min-h-screen flex flex-col items-center justify-center px-8 py-12">
            <EmptyState
              headline={COPY.VAULT_ENTRY_NOT_YOURS_HEADLINE}
              subtitle={COPY.VAULT_ENTRY_NOT_YOURS_SUBTITLE}
              ctaLabel={COPY.VAULT_HEADER}
              onCta={() => navigate(ROUTES.VAULT)}
            />
          </div>
        </AppLayout>
      );
    }
    if (detailState.code === VAULT_ERROR_CODES.NOT_FOUND) {
      return (
        <AppLayout>
          <div className="min-h-screen flex flex-col items-center justify-center px-8 py-12">
            <EmptyState
              headline={COPY.VAULT_ENTRY_DISSOLVED_HEADLINE}
              subtitle={COPY.VAULT_ENTRY_DISSOLVED_SUBTITLE}
              ctaLabel={COPY.VAULT_HEADER}
              onCta={() => navigate(ROUTES.VAULT)}
            />
          </div>
        </AppLayout>
      );
    }
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-8 py-12">
          <EmptyState
            headline={COPY.REJECT_NETWORK_HEADLINE}
            subtitle={detailState.message}
            ctaLabel={COPY.RETRY_CTA}
            onCta={loadDetail}
          />
        </div>
      </AppLayout>
    );
  }

  // Synthesize the focus entry from the detail (the index list isn't needed
  // here — there's no scan strip).
  const detail = detailState.detail;
  const focusEntry = {
    scan_id: scanId,
    timestamp: detail.timestamp,
    taken_at: detail.taken_at,
    thumbnail_url: detail.processed_image_url,
    overall_score: detail.verdict.overall_score,
    top_3_improvement_vectors: detail.verdict.top_3_improvement_vectors,
  };

  const headerDate = focusEntry.taken_at || focusEntry.timestamp;

  return (
    <AppLayout
      header={(
        <>
        {/* Desktop header — title left, date right-aligned */}
        <header className="hidden lg:flex items-center justify-between gap-4">
          <div className="flex items-center gap-5 min-w-0">
            <BackButton to={ROUTES.VAULT} label={COPY.VAULT_BACK_LABEL} />
            <HeaderTitle
              className="font-display font-light tracking-[0.12em] text-bone leading-none"
              size="text-[74px]"
              sizeScrolled="text-[52px]"
            >
              {COPY.VAULT_SCAN_TITLE}
            </HeaderTitle>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-bone text-[26px]" style={{ lineHeight: 1.1 }}>
              {fmtDetailDate(headerDate)}
            </div>
            <div className="font-display text-bone-dim mt-0.5" style={{ fontSize: 14 }}>
              {fmtDetailTime(headerDate)}
            </div>
          </div>
        </header>

        {/* Mobile header — centered title + date subtitle (matches Vault/Create) */}
        <div className="lg:hidden text-center">
          <div className="relative flex items-center justify-center">
            <BackButton
              to={ROUTES.VAULT}
              label={COPY.VAULT_BACK_LABEL}
              className="absolute left-0 top-1/2 -translate-y-1/2"
            />
            <HeaderTitle
              className="font-display font-normal tracking-[0.3em] text-[#E8E2D6] pl-[0.3em]"
              size="text-[27px]"
              sizeScrolled="text-[20px]"
            >
              {COPY.VAULT_SCAN_TITLE}
            </HeaderTitle>
          </div>
          <p className="font-display italic text-sm text-bone-dim mt-1.5 m-0">
            {fmtDetailDate(headerDate)} · {fmtDetailTime(headerDate)}
          </p>
        </div>
        </>
      )}
    >
      <div className="px-5 lg:px-12 pb-10 lg:pb-14">
        {celebrate && (
          <ScanRevealModal
            open
            score10={Number(detailState.detail.verdict?.overall_score || 0) / 10}
            percentileTopText={percentileTopText(detailState.detail.learned_percentile)}
            // No delta: arriving here from Explore means this scan was
            // taken some time ago and the "since last scan" framing would be
            // wrong. The score itself is the payoff.
            deltaText={null}
            onViewScore={() => setSearchParams({}, { replace: true })}
            onReviewRoutine={() => navigate(ROUTES.ROUTINE)}
            onDismiss={() => setSearchParams({}, { replace: true })}
          />
        )}
        <VaultFocusView
          focusEntry={focusEntry}
          focusDetail={detail}
          onImageError={handleImageError}
        />
      </div>
    </AppLayout>
  );
};

export default VaultEntry;
